import {
  NavigationProp,
  ParamListBase,
  useNavigation,
} from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import { useCallback } from "react";
import { useFragment } from "react-relay";

import { ClubStackNavigatorParamList } from "../../../navigators/ClubNavigator";
import { showLessonFacilitySwitcherModalAtom } from "../../Lessons/LessonFacilitySwitcher";
import {
  sessionCurrentClubSlugAtom,
  useIsCurrentUserPublic,
} from "../../Session";
import { navigationRef_atom } from "../atoms";
import {
  getNavigationForRoute,
  RouteLookupNavigation,
} from "../utils/getNavigationForRoute";
import { useLessonTabHandler_session$key } from "./__generated__/useLessonTabHandler_session.graphql";

export const useLessonTabHandler = (
  sessionRef: useLessonTabHandler_session$key | null,
  navigationOverride?: RouteLookupNavigation
) => {
  const setShowLessonFacilitySwitcherModal = useSetAtom(
    showLessonFacilitySwitcherModalAtom
  );
  const session = useFragment(useLessonTabHandlerFragmentString, sessionRef);

  const contextNavigation = useNavigation<NavigationProp<ParamListBase>>();
  const navigation = navigationOverride ?? contextNavigation;
  const navigationRef = useAtomValue(navigationRef_atom);
  const clubSlug = useAtomValue(sessionCurrentClubSlugAtom);

  const isCurrentUserPublic = useIsCurrentUserPublic(session ?? null);

  const facilities =
    session?.currentClientProfile?.member?.club?.facilities?.edges?.flatMap(
      (e) => (e?.node ? [e.node] : [])
    ) ?? [];

  const lessonFacilities = facilities.filter((facility) => {
    return (
      facility.isLessonsEnabled &&
      (isCurrentUserPublic
        ? facility.publicLessonsPermissionSet?.patronAccess !== "NONE"
        : facility.privateLessonsPermissionSet?.patronAccess !== "NONE")
    );
  });

  const lessonTabHandler = useCallback(() => {
    if (lessonFacilities.length === 1) {
      if (clubSlug && navigationRef?.isReady()) {
        navigationRef.navigate("Club", {
          clubSlug,
          screen: "Lessons",
          params: {
            facilitySlug: lessonFacilities[0].slug,
            screen: "LessonsHome",
          },
        });

        return;
      }

      const clubNavigation = navigationOverride?.getParent?.<
        NavigationProp<ClubStackNavigatorParamList>
      >()
        ? navigationOverride.getParent<
            NavigationProp<ClubStackNavigatorParamList>
          >()
        : getNavigationForRoute<ClubStackNavigatorParamList>(
            navigation,
            "Lessons"
          );

      clubNavigation?.navigate("Lessons", {
        facilitySlug: lessonFacilities[0].slug,
        screen: "LessonsHome",
      });
    } else {
      setShowLessonFacilitySwitcherModal(true);
    }
  }, [
    clubSlug,
    navigation,
    navigationRef,
    navigationOverride,
    lessonFacilities,
    setShowLessonFacilitySwitcherModal,
  ]);

  return lessonTabHandler;
};

const useLessonTabHandlerFragmentString = graphql`
  fragment useLessonTabHandler_session on Session {
    ...useIsCurrentUserPublic_session
    currentClientProfile {
      member {
        club {
          facilities {
            edges {
              node {
                slug
                isLessonsEnabled
                publicLessonsPermissionSet {
                  patronAccess
                }
                privateLessonsPermissionSet {
                  patronAccess
                }
              }
            }
          }
        }
      }
    }
  }
`;
